import { supabase } from '../supabaseClient';

export const runComfyDeployWorkflow = async (deploymentId, inputs, onProgress) => {
  try {
    // Validate deploymentId exists and is a string
    if (!deploymentId || typeof deploymentId !== 'string') {
      throw new Error('Invalid deployment ID: must be a non-empty string');
    }

    // Special handling for video generation mode
    const isVideoMode = inputs.mode === 'video';
    const videoSpecificInputs = isVideoMode ? {
      camera_movement: inputs.camera_movement || 'pan_right',
      duration: inputs.duration || '4',
      quality: inputs.quality || 'standard',
      video_format: 'webp'
    } : {};

    // Get the current session for authentication
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('User not authenticated');
    }

    // Call the Supabase function to start the ComfyDeploy workflow
    const { data: result, error: functionError } = await supabase.functions.invoke('comfy-deploy-run', {
      body: {
        deployment_id: deploymentId,
        inputs: {
          ...inputs,
          ...videoSpecificInputs,
          batch_number: inputs.batch_size || 1
        }
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (functionError) {
      console.error('Supabase function error:', functionError);
      throw new Error(`ComfyDeploy API error: ${functionError.message}`);
    }

    if (!result) {
      console.error('No response received from ComfyDeploy API');
      throw new Error('No response received from ComfyDeploy API');
    }

    // Log the full response for debugging
    console.log('ComfyDeploy API Response:', result);

    if (result.error) {
      throw new Error(`ComfyDeploy API error: ${result.error}`);
    }

    if (!result.run_id) {
      throw new Error('No run_id received from ComfyDeploy API');
    }

    const runId = result.run_id;
    // console.log(`Run created with ID: ${runId}`);

    // Poll for results with error handling
      let runResult;
      let lastProgress = 0;
      let pollCount = 0;
      const MAX_POLLS = 120; // Maximum number of polls (6 minutes at 3 second intervals)

      while (pollCount < MAX_POLLS) {
        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait for 3 seconds between polls
        pollCount++;

        try {
          // Call the Supabase function to get the run status
          const { data: statusResult, error: statusError } = await supabase.functions.invoke(`comfy-deploy-status/${runId}`, {
            body: {},
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });

          if (statusError) {
            console.error('Status function error:', statusError);
            throw new Error(`Status check failed: ${statusError.message}`);
          }

          runResult = statusResult;
          
          if (runResult.status === 'success') {
            console.log('Workflow completed successfully');
            // Ensure we show 100% progress on success

            // Add success status to outputs for client-side verification
            const outputsWithStatus = runResult.outputs ?
              runResult.outputs.map(output => ({
                ...output,
                status: 'success'
              })) : [];

            // Handle video output differently
            if (isVideoMode && outputsWithStatus.length > 0) {
              // Log the full output structure for debugging
              console.log('Video workflow success. Full outputs:', JSON.stringify(outputsWithStatus));

              // Check for any WebP output with images (WebP can be animated)
              const webpOutput = outputsWithStatus.find(output =>
                output.data?.images?.[0]?.url &&
                output.data?.images?.[0]?.url.endsWith('.webp')
              );

              if (webpOutput) {
                // Create a simplified output object with the URL directly accessible
                const simplifiedOutput = {
                  ...webpOutput,
                  url: webpOutput.data.images[0].url,
                  type: 'animated-webp',
                  status: 'success'
                };
                console.log('Found WebP output:', simplifiedOutput);
                onProgress(1.0, simplifiedOutput);
                return outputsWithStatus;
              }

              // Fallback to previous method
              const videoOutput = outputsWithStatus.find(output =>
                output.type === 'video' ||
                (output.url && (output.url.endsWith('.webp') || output.url.endsWith('.mp4')))
              );

              if (videoOutput) {
                console.log('Found video output:', videoOutput);
                onProgress(1.0, videoOutput);
                return outputsWithStatus;
              }

              // Deep search for any URL in the outputs
              for (const output of runResult.outputs) {
                // Check if output has a data property with nested structure
                if (output.data) {
                  // Look for images array
                  if (Array.isArray(output.data.images) && output.data.images.length > 0) {
                    const imageWithUrl = output.data.images.find(img => img.url);
                    if (imageWithUrl) {
                      // Check if this is a WebP image (which can be animated)
                      const isWebp = imageWithUrl.url.endsWith('.webp');

                      console.log('Found image URL in nested data:', imageWithUrl, 'WebP:', isWebp);
                      onProgress(1.0, {
                        ...output,
                        url: imageWithUrl.url,
                        type: isWebp ? 'animated-webp' : 'image'
                      });
                      return runResult.outputs;
                    }
                  }
                }
              }

              // If we still haven't found a usable output, pass the first output
              console.log('No specific video output found, using first output');
              onProgress(1.0, runResult.outputs[0]);
              return runResult.outputs;
            } else {
              onProgress(1.0, runResult.outputs?.[0]);
              return runResult.outputs;
            }
          } else if (runResult.status === 'failed') {
            throw new Error(`Workflow failed: ${runResult.error || 'Unknown error'}`);
          } else if (runResult.status === 'running' || runResult.status === 'uploading') {
            // Always update progress if it's defined, even if same as last progress
            if (runResult.progress !== undefined) {
              // Always update progress to ensure UI stays in sync
              lastProgress = runResult.progress;
              console.log('Progress update:', lastProgress);

              // Call onProgress with the latest progress value
              onProgress(lastProgress, runResult.outputs?.[0]);

              // Force a second update after a small delay to ensure UI updates
              setTimeout(() => {
                onProgress(lastProgress, runResult.outputs?.[0]);
              }, 100);
            }
            // Check for LLM response in the progress payload
            if (runResult.payload && runResult.payload.llm_response) {
              onProgress(lastProgress, null, runResult.payload.llm_response);
            }
          }
        } catch (pollError) {
          console.error('Error polling for results:', pollError);

          // If we get multiple consecutive errors, we might want to stop polling
          if (pollCount > 10) {
            throw new Error(`Failed to poll for results after multiple attempts: ${pollError.message}`);
          }
        }
      }

      // If we've reached the maximum number of polls, return what we have or throw an error
      if (runResult && runResult.outputs && runResult.outputs.length > 0) {
        console.warn('Reached maximum polling attempts but workflow has outputs. Returning available results.');
        
        // Try to extract a usable URL from the outputs
        for (const output of runResult.outputs) {
          if (output.data && Array.isArray(output.data.images) && output.data.images.length > 0) {
            const imageWithUrl = output.data.images.find(img => img.url);
            if (imageWithUrl) {
              console.log('Found image URL in timeout data:', imageWithUrl.url);
              onProgress(1.0, { 
                ...output, 
                url: imageWithUrl.url, 
                type: 'animated-webp',  // Assume it's animated for webp files
                status: 'success' 
              });
              return runResult.outputs;
            }
          }
        }
        
        // Fallback to just returning the first output
        onProgress(1.0, runResult.outputs[0]);
        return runResult.outputs;
      }
      
      throw new Error('Workflow did not complete within the expected time (6 minutes)');
  } catch (error) {
    console.error('Error running Comfy Deploy workflow:', error);

    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(`Unexpected Comfy Deploy error: ${JSON.stringify(error)}`);
    }
  }
};
